import { BaseService } from "../services/baseService";
import { logger, LOG_CATEGORIES } from "../utils/logger";
// import { gamificationService } from "../services/gamificationService";

class InvestmentService extends BaseService {
    constructor() {
        super("investments", "investment");
    }

    async createInvestment(investmentData) {
        try {
            await logger.info(LOG_CATEGORIES.INVESTMENT, "Creating investment", { 
                userId: investmentData.userId,
                opportunityId: investmentData.opportunityId,
                amount: investmentData.amount 
            });

            const investmentId = this.generateId();
            const investmentDoc = {
                ...investmentData,
                status: "active",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            const result = await this.create(investmentId, investmentDoc);

            // Log investment creation for audit
            await this.logAuditEvent("created", {
                investmentId: result.id,
                userId: investmentData.userId,
                opportunityId: investmentData.opportunityId,
                amount: investmentData.amount,
                expectedReturn: investmentData.expectedReturn
            });

            // Trigger gamification checks after successful investment creation
            try {
                // await gamificationService.checkAndUpdateUserAchievements(
                //   investmentData.userId,
                //   investmentData.amount,
                //   investmentData
                // );
                await logger.debug(LOG_CATEGORIES.INVESTMENT, "Gamification check completed", { 
                    userId: investmentData.userId 
                });
            } catch (error) {
                await logger.warn(LOG_CATEGORIES.INVESTMENT, "Gamification check failed", error, { 
                    userId: investmentData.userId 
                });
                // Don't fail the investment creation if gamification fails
            }

            await logger.info(LOG_CATEGORIES.INVESTMENT, "Investment created successfully", { 
                investmentId: result.id,
                userId: investmentData.userId 
            });

            return result.id;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to create investment", error, { 
                userId: investmentData.userId,
                opportunityId: investmentData.opportunityId 
            });
            throw new Error("Failed to create investment");
        }
    }

    async getUserInvestments(userId) {
        try {
            const investments = await this.getAll(
                { userId },
                { orderBy: { field: "createdAt", direction: "desc" } }
            );

            await logger.debug(LOG_CATEGORIES.INVESTMENT, "User investments retrieved", { 
                userId, 
                count: investments.length 
            });

            return investments;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to get user investments", error, { userId });
            throw new Error("Failed to get user investments");
        }
    }

    async getInvestmentById(investmentId) {
        try {
            const investment = await this.getById(investmentId);
            
            if (!investment) {
                await logger.warn(LOG_CATEGORIES.INVESTMENT, "Investment not found", { investmentId });
                throw new Error("Investment not found");
            }

            await logger.debug(LOG_CATEGORIES.INVESTMENT, "Investment retrieved", { investmentId });
            return investment;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to get investment", error, { investmentId });
            throw new Error("Failed to get investment");
        }
    }

    async getInvestmentsByOpportunity(opportunityId) {
        try {
            const investments = await this.getAll(
                { opportunityId },
                { orderBy: { field: "createdAt", direction: "desc" } }
            );

            await logger.debug(LOG_CATEGORIES.INVESTMENT, "Opportunity investments retrieved", { 
                opportunityId, 
                count: investments.length 
            });

            return investments;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to get opportunity investments", error, { opportunityId });
            throw new Error("Failed to get opportunity investments");
        }
    }

    async updateInvestmentStatus(investmentId, status) {
        try {
            const updateData = {
                status: status,
                updatedAt: new Date().toISOString()
            };

            const result = await this.update(investmentId, updateData);

            // Log status update for audit
            await this.logAuditEvent("status_updated", {
                investmentId,
                status,
                previousStatus: "unknown" // Would need to get current status first
            });

            await logger.info(LOG_CATEGORIES.INVESTMENT, "Investment status updated", { 
                investmentId, 
                status 
            });

            return result;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to update investment status", error, { 
                investmentId, 
                status 
            });
            throw new Error("Failed to update investment status");
        }
    }

    // Calculate total investment value including returns
    async calculatePortfolioValue(userId) {
        try {
            const investments = await this.getUserInvestments(userId);
            let totalInvested = 0;
            let totalValue = 0;

            for (const investment of investments) {
                if (investment.status === "active" || investment.status === "completed") {
                    totalInvested += investment.amount || 0;
                    // Calculate current value based on opportunity performance
                    // For now, using a simple calculation - in production this would fetch real-time data
                    const currentValue = investment.amount * (1 + (investment.expectedReturn || 0.08) / 100);
                    totalValue += currentValue;
                }
            }

            const portfolioValue = {
                totalInvested,
                totalValue,
                totalReturn: totalValue - totalInvested,
                returnPercentage: totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested) * 100 : 0
            };

            await logger.debug(LOG_CATEGORIES.INVESTMENT, "Portfolio value calculated", { 
                userId, 
                ...portfolioValue 
            });

            return portfolioValue;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to calculate portfolio value", error, { userId });
            return {
                totalInvested: 0,
                totalValue: 0,
                totalReturn: 0,
                returnPercentage: 0
            };
        }
    }

    // Get investment statistics
    async getInvestmentStatistics(userId = null) {
        try {
            const filters = userId ? { userId } : {};
            const allInvestments = await this.getAll(filters);
            
            const statistics = {
                total: allInvestments.length,
                active: allInvestments.filter(inv => inv.status === "active").length,
                completed: allInvestments.filter(inv => inv.status === "completed").length,
                cancelled: allInvestments.filter(inv => inv.status === "cancelled").length,
                totalAmount: allInvestments.reduce((sum, inv) => sum + (inv.amount || 0), 0),
                averageAmount: allInvestments.length > 0 ? 
                    allInvestments.reduce((sum, inv) => sum + (inv.amount || 0), 0) / allInvestments.length : 0
            };

            await logger.debug(LOG_CATEGORIES.INVESTMENT, "Investment statistics retrieved", statistics);

            return statistics;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to get investment statistics", error, { userId });
            throw new Error("Failed to get investment statistics");
        }
    }

    // Get investments by status
    async getInvestmentsByStatus(status, userId = null) {
        try {
            const filters = { status };
            if (userId) {
                filters.userId = userId;
            }

            const investments = await this.getAll(filters, {
                orderBy: { field: "createdAt", direction: "desc" }
            });

            await logger.debug(LOG_CATEGORIES.INVESTMENT, "Investments by status retrieved", { 
                status, 
                userId, 
                count: investments.length 
            });

            return investments;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to get investments by status", error, { status, userId });
            throw new Error("Failed to get investments by status");
        }
    }

    // Cancel investment
    async cancelInvestment(investmentId, reason = null) {
        try {
            const updateData = {
                status: "cancelled",
                cancelledAt: new Date().toISOString(),
                cancellationReason: reason,
                updatedAt: new Date().toISOString()
            };

            const result = await this.update(investmentId, updateData);

            // Log cancellation for audit
            await this.logAuditEvent("cancelled", {
                investmentId,
                reason,
                cancelledAt: updateData.cancelledAt
            });

            await logger.info(LOG_CATEGORIES.INVESTMENT, "Investment cancelled", { 
                investmentId, 
                reason 
            });

            return result;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.INVESTMENT, "Failed to cancel investment", error, { investmentId, reason });
            throw new Error("Failed to cancel investment");
        }
    }
}

export const investmentService = new InvestmentService(); 